<template>
  <div>
    <PseudoNotificationComponent></PseudoNotificationComponent>
    <div class="pt-0 px-10 pb-0 flex items-center justify-between h-14 relative max-w-screen-2xl mx-auto">
      <div class="flex flex-row gap-12 items-center justify-start shrink-0 relative">
        <CompanyLogo />
        <div class="flex gap-6 items-center justify-start shrink-0 relative">
          <RecruiterNavTab
            data-testid="recruiter-nav-interviews"
            v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE"
            :to="homePageRoute"
            :label="$t('homePage.title')"
          />
          <RecruiterNavTab
            data-testid="recruiter-nav-hiring-plan"
            v-permissions="getHiringPlanPermissions"
            v-if="canShowHiringPlan"
            :to="hiringPlanRoute"
            :label="$t('recruiterNavigation.hiringPlan')"
          />
          <RecruiterNavTab
            data-testid="recruiter-nav-jobs"
            v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE"
            :to="'/recruiter/jobs'"
            :label="$t('recruiterNavigation.jobs')"
          />
          <RecruiterNavTab
            data-testid="recruiter-nav-candidates"
            v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE"
            :to="`/recruiter/candidates`"
            :label="$t('recruiterNavigation.candidates')"
          />
          <RecruiterNavTab
            data-testid="recruiter-nav-referrals"
            :to="`/recruiter/employee-portal`"
            :label="$t('recruiterNavigation.employeePortal')"
          />
          <RecruiterNavTab
            v-permissions="hasAccessToReports"
            data-testid="recruiter-nav-reports"
            :to="`/recruiter/reports`"
            :label="$t('recruiterNavigation.reports')"
          />
        </div>
      </div>
      <div class="flex flex-row gap-8 items-center justify-start shrink-0 relative">
        <div class="flex flex-row gap-4 items-center justify-start shrink-0 relative">
          <Inbox v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE" />
          <ElTooltip
            v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE"
            :content="$t('recruiterNavigation.tooltipContent.downloadChromeExtension')"
            placement="top">
            <RecruiterNavTab
              :to="`https://chrome.google.com/webstore/detail/whitecarrot-extension/kihdfipgbnhlckakgichedgelmkpagck?hl=en`"
              :icon='chromeIcon()'
            />
          </ElTooltip>
          <ElTooltip v-if="UserModule().roleAccess !== RolesEnum.COMPANY_EMPLOYEE" :content="$t('recruiterNavigation.tooltipContent.helpCenter')">
            <div @click="handleBot()">
              <span v-html="helpCenterIcon()" class="cursor-pointer"></span>
            </div>
          </ElTooltip>
          <ElTooltip data-testid="setting" effect="dark" :content="$t('recruiterNavigation.tooltipContent.settings')" placement="top">
            <RecruiterNavTab :to="`/recruiter-settings`" :icon='settingsIcon()' />
          </ElTooltip>
          <LanguageSwitcher v-if="showLanguageSupport" />
        </div>
        <ChatBotDialog
          :close-sync="isVisible"
          @close-jdialog="isVisible = false"
        />
        <RecruiterNavbarDropdownMenu :user="user"/>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { UserModule } from '@/store/modules/user'
import RecruiterNavTab from '@/components/RecruiterNavbar/RecruiterNavTab.vue'
import { Component, Vue } from 'vue-property-decorator'
import CompanyLogo from '@/views/logos/company-logo.vue'
import PseudoNotificationComponent from '@/components/PseudoNotificationComponent.vue'
import RecruiterNavbarDropdownMenu from '@/components/RecruiterNavbar/RecruiterNavbarDropdownMenu.vue'
import { LanguageEnum, LanguageModule } from '@/store/modules/language'
import { chromeIcon, helpCenterIcon, settingsIcon } from '@/components/RecruiterNavbar/svg.icons'
import LanguageSwitcher from '@/components/UILibrary/LanguageSwitcher.vue'
import { CompanyModule } from '@/store/modules/company'
import WcDialog from '@/components/UILibrary/WcDialog.vue'
import WcTypography from '@/components/UILibrary/Typography/WcTypography.vue'
import ChatBotDialog from '@/components/Chatbot/ChatBotDialog.vue'
import Inbox from '@/views/home-page/inbox/Inbox.vue'
import { PermissionsEnum } from '@/permissions/permissions-enum'
import { RolesEnum } from '@/views/recruiter/models/roles-model'
import { hiringPlanRoute, homePageRoute } from '@/routes/urls'
import { isMobile } from '@/utils/general'

@Component({
  name: 'RecruiterNav',
  computed: {
    PermissionsEnum() {
      return PermissionsEnum
    },
    RolesEnum() {
      return RolesEnum
    },
    LanguageEnum() {
      return LanguageEnum
    },
    getCurrentLanguage() {
      return LanguageModule.language
    },
    showLanguageSupport() {
      return CompanyModule.haveLanguageSupport
    },
    hiringPlanRoute() {
      return hiringPlanRoute
    },
    homePageRoute() {
      return homePageRoute
    },
    canShowHiringPlan() {
      return CompanyModule.isRequisitionEnabled
    },
    getHiringPlanPermissions() {
      return [
        PermissionsEnum.COMPANY_MANAGE_HIRING_PLAN,
        PermissionsEnum.COMPANY_HIRING_PLAN_REQUISITIONS_CREATE,
        PermissionsEnum.COMPANY_HIRING_PLAN_DEFAULT_APPROVAL_FLOW_APPROVER,
        PermissionsEnum.COMPANY_HIRING_PLAN_OPENING_APPROVAL_FLOW_APPROVER,
        PermissionsEnum.COMPANY_HIRING_PLAN_REQUISITIONS_LIST
      ]
    }
  },
  methods: {
    isMobile,
    settingsIcon() {
      return settingsIcon
    },
    helpCenterIcon() {
      return helpCenterIcon
    },
    chromeIcon() {
      return chromeIcon
    },
    LanguageModule() {
      return LanguageModule
    },
    UserModule() {
      return UserModule
    }
  },
  props: {
    user: Object
  },
  components: {
    Inbox,
    ChatBotDialog,
    WcTypography,
    WcDialog,
    LanguageSwitcher,
    RecruiterNavbarDropdownMenu,
    PseudoNotificationComponent,
    CompanyLogo,
    RecruiterNavTab
  }
})
export default class extends Vue {
  private companyId = '';
  isVisible = false;
  mounted(): void {
    this.initNavbar()
  }

  initNavbar(): void {
    this.companyId = UserModule.companyId || ''
  }

  handleBot(): void {
    this.isVisible = true
  }

  get hasAccessToReports(): PermissionsEnum[] {
    return [PermissionsEnum.MANAGE_COMPANY_REPORTS]
  }
}
</script>
<style scoped>
*{
  font-family: "Inter", sans-serif;
}
</style>
<style>
.navbar-dropdown-menu {
  z-index: 1021;
}
</style>
